// Thin client for the same-origin /api. Never hard-codes the domain.

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body: unknown = null,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, headers, ...rest } = init;
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    ...rest,
    headers: { accept: 'application/json', ...(json !== undefined ? { 'content-type': 'application/json' } : {}), ...headers },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  if (res.status === 204) return undefined as T;
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const b = (body ?? {}) as { error?: string; message?: string };
    throw new ApiError(res.status, b.error ?? 'error', b.message ?? `Request failed (${res.status})`, body);
  }
  return body as T;
}

export interface ServerConfig {
  env: string;
  features: { accounts: boolean; billing: boolean; microsoft?: boolean };
  licensePrice: { amount: number; currency: string };
  /** Private test site: whether the signed-in account may use it ('open' = not restricted). */
  access?: 'open' | 'allowed' | 'denied';
  /** Where the high-quality demo video lives; null = the copies bundled in /demo. */
  demoMediaUrl?: string | null;
  /** Tile server for the photo-location map ({z}/{x}/{y} template) and its required credit. */
  map?: { tileUrl: string; attribution: string; maxZoom: number; styles?: MapStyle[] };
}

/** A map look for the photo-location map ({s} in tileUrl = a/b/c servers). */
export interface MapStyle {
  id: string;
  name: string;
  tileUrl: string;
  attribution: string;
  maxZoom: number;
}

export interface Place {
  city: string | null;
  district: string | null;
  country: string | null;
  countryCode: string | null;
}

export interface User {
  id: string;
  email: string;
  license: 'none' | 'lifetime';
  /** May manage house ads at /admin. */
  admin?: boolean;
}

export interface FeedItem {
  title: string;
  link: string | null;
  published: string | null;
  summary?: string | null;
  image?: string | null;
}

export interface CalendarEvent {
  uid: string;
  title: string;
  location: string | null;
  start: string;
  end: string;
  allDay: boolean;
  /** Set by provider calendars (Outlook) that carry their own name/colour. */
  calendar?: { id: string; name: string; color: string };
}

export interface OutlookCalendar {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  owner: string | null;
  /** The connected Microsoft account it belongs to. */
  account?: string;
  accountEmail?: string | null;
}

/** One connected Microsoft account (a user can connect several, e.g. work and personal). */
export interface MicrosoftAccount {
  id: string;
  email: string | null;
  connected: boolean;
  tasks: boolean;
  work: boolean;
  workAccess: boolean;
  /** Colleagues' names can be read (Planner assignees). */
  people?: boolean;
  /** Power BI reports and dashboards can be read. */
  powerbi?: boolean;
}

export type PowerBiKind = 'report' | 'dashboard';

/** One account's Power BI: its workspaces (null id = My workspace) with reports and dashboards. */
export interface PowerBiAccountItems {
  account: string;
  accountEmail: string | null;
  workspaces: Array<{
    id: string | null;
    name: string;
    reports: Array<{ id: string; name: string }>;
    dashboards: Array<{ id: string; name: string }>;
  }>;
}

/** A Planner task's details for the pop-up. */
export interface PlannerTaskDetails {
  description: string;
  checklist: Array<{ title: string; done: boolean }>;
  links: Array<{ url: string; name: string }>;
}

const plannerBase = (planId: string, canvasId?: string | null) =>
  `${canvasId ? `/canvases/${canvasId}` : '/microsoft'}/planner/plans/${encodeURIComponent(planId)}`;
const accountQuery = (account?: string, canvasId?: string | null) => (account && !canvasId ? `?${new URLSearchParams({ account })}` : '');

/** A person's photo on a board (404 when there's none – show initials). */
export const plannerPhotoUrl = (planId: string, userId: string, account?: string, canvasId?: string | null) =>
  `/api${plannerBase(planId, canvasId)}/people/${encodeURIComponent(userId)}/photo${accountQuery(account, canvasId)}`;

/** What the browser needs to embed a report or dashboard. */
export interface PowerBiEmbed {
  kind: PowerBiKind;
  id: string;
  name: string;
  embedUrl: string;
  accessToken: string;
  /** aad: the viewer's own token; embed: a token for this one item. */
  tokenType: 'aad' | 'embed';
  expiresAt: string;
}

export interface DriveFolder {
  id: string;
  name: string;
  childCount: number;
}

/** Work/school: folders shared with the user and SharePoint sites they can open. */
export interface DriveLocations {
  shared: Array<{ drive: string; id: string; name: string }>;
  sites: Array<{ id: string; name: string; url: string | null }>;
}

const query = (params: Record<string, string | undefined>) =>
  new URLSearchParams(Object.entries(params).filter((e): e is [string, string] => e[1] !== undefined)).toString();

export interface DriveListing {
  folder: { id: string | null; name: string; parentId: string | null };
  folders: DriveFolder[];
  mediaCount: number;
}

export interface DriveMedia {
  id: string;
  name: string;
  kind: 'image' | 'video';
  mime: string | null;
  takenAt: string | null;
  width?: number | null;
  height?: number | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface WeatherWarning {
  id: string;
  event: string;
  area: string;
  level: 'yellow' | 'orange' | 'red' | 'unknown';
  severity: string;
  onset: string | null;
  expires: string | null;
  link: string | null;
}

/** A group people join by invite, to share canvases (Settings → Sharing). */
export interface Group {
  id: string;
  name: string;
  role: 'owner' | 'member';
  members: Array<{ userId: string; email: string; name: string | null; role: 'owner' | 'member'; me: boolean }>;
}

/** A saved canvas shared with groups. `snapshot` comes with GET /canvases/:id only. */
export interface SharedCanvas {
  id: string;
  name: string;
  owner: { email: string; name: string | null; me: boolean };
  include: import('../lib/canvasContent').Include;
  version: number;
  updatedAt: string;
  permissions: { owner: boolean; edit: boolean; share: boolean };
  shares: Array<{ groupId: string; groupName: string; canEdit: boolean; canShare: boolean; mine: boolean }>;
  snapshot?: Record<string, unknown>;
}

/** A screen showing KCL Canvas for the account (Settings → Screens). */
export interface Screen {
  deviceId: string;
  name: string;
  kind: string;
  info: { screen?: { width: number; height: number } | null; showing?: string | null };
  /** Saved layout shown on it; null = the main setup. */
  presetId: string | null;
  /** Cycles through several instead (overrides presetId). */
  rotation: ScreenRotation | null;
  /** null = the schedule for all screens; 'always' = always on; else a schedule id. */
  scheduleId: string | null;
  lastSeen: string;
  added: string;
}

/** Things a screen cycles through ('main' | saved layout id | 'shared:<canvas id>'), each for intervalSec. */
export interface ScreenRotation {
  items: string[];
  intervalSec: number;
}

/** On periods per weekday (0 = Sunday … 6 = Saturday), "HH:MM" local time; end ≤ start runs past midnight. */
export interface ScreenSchedule {
  id: string;
  name: string;
  days: Array<Array<{ from: string; to: string }>>;
}

export interface ScreenSchedules {
  schedules: ScreenSchedule[];
  /** The schedule all screens follow unless they have their own; null or 'always' = always on. */
  defaultId: string | null;
}

export interface TodoList {
  id: string;
  name: string;
  kind: string;
  /** The connected Microsoft account it belongs to. */
  account?: string;
  accountEmail?: string | null;
}

export interface TodoTask {
  id: string;
  title: string;
  done: boolean;
  important: boolean;
  createdAt: string | null;
  completedAt: string | null;
  due: string | null;
}

export interface PlannerPlan {
  id: string;
  name: string;
  account?: string;
  accountEmail?: string | null;
}

export type PlannerProgress = 'notStarted' | 'inProgress' | 'completed';
export type PlannerPriority = 'urgent' | 'important' | 'medium' | 'low';

export interface PlannerCard {
  id: string;
  title: string;
  bucketId: string | null;
  progress: PlannerProgress;
  priority: PlannerPriority;
  start: string | null;
  due: string | null;
  createdAt: string | null;
  completedAt: string | null;
  /** Label slots ("category1" … "category25"); names are in the board's `labels`. */
  labels: string[];
  assignees: string[];
  checklist: { done: number; total: number } | null;
}

/** A Planner plan as the widget draws it (server/src/lib/planner.ts). */
export interface PlannerBoard {
  plan: { id: string; title: string };
  buckets: Array<{ id: string; name: string }>;
  labels: Record<string, string>;
  tasks: PlannerCard[];
  people: Record<string, string>;
  me: string | null;
  /** When it was read from Planner. */
  asOf?: string;
  /** An older copy, because Planner is limiting requests or briefly unavailable. */
  stale?: boolean;
}

/** To Do is reached directly, or through a shared canvas (`canvasId`) with its owner's connection. */
const todoBase = (canvasId?: string | null) => (canvasId ? `/canvases/${canvasId}/todo` : '/microsoft/todo');
const todo = (listId: string, taskId?: string, account?: string, canvasId?: string | null) =>
  `${todoBase(canvasId)}/lists/${encodeURIComponent(listId)}/tasks${taskId ? `/${encodeURIComponent(taskId)}` : ''}${
    account ? `?${new URLSearchParams({ account })}` : ''
  }`;

export type ScreenPatch = { name?: string; presetId?: string | null; rotation?: ScreenRotation | null; scheduleId?: string | null };

export interface AdminAd {
  id: string;
  advertiser: string;
  headline: string;
  body: string;
  cta: string;
  url: string;
  imageBrief: string | null;
  active: boolean;
  sort: number;
  image: string | null;
}

/** How often free-tier screens show an ad, for how long, and in what order (set at /admin). */
export interface AdSchedule {
  intervalMin: number;
  durationSec: number;
  order: 'random' | 'sequence';
}

export const DEFAULT_AD_SCHEDULE: AdSchedule = { intervalMin: 60, durationSec: 30, order: 'random' };

export type AdInput = Partial<Omit<AdminAd, 'id' | 'image'>> & { image?: string | null };

export const endpoints = {
  ads: () => api<{ ads: AdminAd[]; schedule?: AdSchedule }>('/ads'),
  adminAds: () => api<{ ads: AdminAd[]; schedule: AdSchedule }>('/admin/ads'),
  adminSetAdSchedule: (schedule: AdSchedule) =>
    api<{ schedule: AdSchedule }>('/admin/ads/schedule', { method: 'PUT', json: schedule }),
  adminCreateAd: (ad: AdInput) => api<{ ad: AdminAd }>('/admin/ads', { method: 'POST', json: ad }),
  adminUpdateAd: (id: string, ad: AdInput) => api<{ ad: AdminAd }>(`/admin/ads/${id}`, { method: 'PATCH', json: ad }),
  adminDeleteAd: (id: string) => api<void>(`/admin/ads/${id}`, { method: 'DELETE' }),
  adminImportAds: (ads: AdInput[]) => api<{ added: number }>('/admin/ads/import', { method: 'POST', json: { ads } }),
  todoLists: (canvasId?: string | null) => api<{ lists: TodoList[] }>(`${todoBase(canvasId)}/lists`),
  todoTasks: (listId: string, account?: string, canvasId?: string | null) => api<{ tasks: TodoTask[] }>(todo(listId, undefined, account, canvasId)),
  todoAdd: (listId: string, title: string, account?: string, canvasId?: string | null) =>
    api<{ task: TodoTask }>(todo(listId, undefined, account, canvasId), { method: 'POST', json: { title } }),
  todoSetDone: (listId: string, taskId: string, done: boolean, account?: string, canvasId?: string | null) =>
    api<{ task: TodoTask }>(todo(listId, taskId, account, canvasId), { method: 'PATCH', json: { done } }),
  todoDelete: (listId: string, taskId: string, account?: string, canvasId?: string | null) =>
    api<void>(todo(listId, taskId, account, canvasId), { method: 'DELETE' }),
  plannerPlans: () => api<{ plans: PlannerPlan[] }>('/microsoft/planner/plans'),
  plannerTaskDetails: (planId: string, taskId: string, account?: string, canvasId?: string | null) =>
    api<{ details: PlannerTaskDetails }>(`${plannerBase(planId, canvasId)}/tasks/${encodeURIComponent(taskId)}${accountQuery(account, canvasId)}`),
  powerbiItems: () => api<{ accounts: PowerBiAccountItems[] }>('/powerbi/items'),
  powerbiPages: (report: string, workspace: string | null, account?: string) =>
    api<{ pages: Array<{ name: string; title: string }> }>(`/powerbi/pages?${query({ report, workspace: workspace ?? undefined, account })}`),
  /** Embed details, with the viewer's own Power BI or through a shared canvas (`canvasId`). */
  powerbiEmbed: (kind: PowerBiKind, id: string, workspace: string | null, account?: string, canvasId?: string | null) =>
    api<{ embed: PowerBiEmbed }>(
      canvasId
        ? `/canvases/${canvasId}/powerbi/embed?${query({ kind, id, workspace: workspace ?? undefined })}`
        : `/powerbi/embed?${query({ kind, id, workspace: workspace ?? undefined, account })}`,
    ),
  /** A Planner board, directly or through a shared canvas (`canvasId`) with its owner's connection. */
  /** `cardOrder`: cards in Planner's board order – the Board view only (it costs Planner a request per task). */
  plannerBoard: (planId: string, account?: string, canvasId?: string | null, cardOrder = false) => {
    const q = query({ account: canvasId ? undefined : account, order: cardOrder ? 'board' : undefined });
    return api<{ board: PlannerBoard }>(`${plannerBase(planId, canvasId)}/board${q ? `?${q}` : ''}`);
  },
  /** A shared canvas's OneDrive folder (by its id in the canvas), with the owner's connection. */
  canvasDriveMedia: (canvasId: string, folder: string) =>
    api<{ items: DriveMedia[]; truncated: boolean }>(`/canvases/${canvasId}/onedrive/media?${query({ folder })}`),
  canvasOutlookCalendar: (canvasId: string, days: number, start?: string) =>
    api<{ events: CalendarEvent[] }>(`/canvases/${canvasId}/outlook/calendar?${query({ days: String(days), start })}`),
  warnings: (country: string, place: string) =>
    api<{ local: WeatherWarning[]; elsewhere: number }>(
      `/feeds/warnings?country=${encodeURIComponent(country)}&place=${encodeURIComponent(place)}`,
    ),
  config: () => api<ServerConfig>('/config'),
  me: () => api<{ user: User | null }>('/auth/me'),
  login: (email: string, password: string) => api<{ user: User }>('/auth/login', { method: 'POST', json: { email, password } }),
  register: (email: string, password: string) =>
    api<{ user: User }>('/auth/register', { method: 'POST', json: { email, password } }),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  deleteAccount: () => api<void>('/auth/account', { method: 'DELETE' }),
  getSettings: () => api<{ data: Record<string, unknown> | null; version: string | null }>('/settings'),
  putSettings: (data: Record<string, unknown>, baseVersion: string | null) =>
    api<{ version: string }>('/settings', { method: 'PUT', json: { data, baseVersion } }),
  checkout: () => api<{ url: string }>('/billing/checkout', { method: 'POST' }),
  /** Signed link that opens checkout for this account on another device (TVs). */
  phoneLink: () => api<{ url: string; expiresAt: string }>('/billing/phone-link', { method: 'POST' }),
  groups: () => api<{ groups: Group[] }>('/groups'),
  createGroup: (name: string) => api<{ group: { id: string; name: string } }>('/groups', { method: 'POST', json: { name } }),
  renameGroup: (id: string, name: string) => api<{ ok: true }>(`/groups/${id}`, { method: 'PATCH', json: { name } }),
  deleteGroup: (id: string) => api<void>(`/groups/${id}`, { method: 'DELETE' }),
  inviteToGroup: (id: string) => api<{ url: string; expiresAt: string }>(`/groups/${id}/invites`, { method: 'POST' }),
  removeMember: (groupId: string, userId: string) => api<void>(`/groups/${groupId}/members/${userId}`, { method: 'DELETE' }),
  joinGroup: (token: string) => api<{ group: { id: string; name: string } }>('/groups/join', { method: 'POST', json: { token } }),
  canvases: () => api<{ canvases: SharedCanvas[] }>('/canvases'),
  canvas: (id: string) => api<{ canvas: SharedCanvas }>(`/canvases/${id}`),
  createCanvas: (body: { name: string; snapshot: Record<string, unknown>; include: Record<string, boolean>; fromPresetId?: string }) =>
    api<{ canvas: SharedCanvas }>('/canvases', { method: 'POST', json: body }),
  saveCanvas: (id: string, snapshot: Record<string, unknown>, baseVersion: number) =>
    api<{ canvas: SharedCanvas }>(`/canvases/${id}`, { method: 'PUT', json: { snapshot, baseVersion } }),
  updateCanvas: (id: string, patch: { name?: string; include?: Record<string, boolean> }) =>
    api<{ canvas: SharedCanvas }>(`/canvases/${id}`, { method: 'PATCH', json: patch }),
  deleteCanvas: (id: string) => api<void>(`/canvases/${id}`, { method: 'DELETE' }),
  shareCanvas: (id: string, groupId: string, rights: { canEdit: boolean; canShare: boolean }) =>
    api<{ canvas: SharedCanvas }>(`/canvases/${id}/shares/${groupId}`, { method: 'PUT', json: rights }),
  unshareCanvas: (id: string, groupId: string) => api<void>(`/canvases/${id}/shares/${groupId}`, { method: 'DELETE' }),
  screenHeartbeat: (body: { deviceId: string; screen?: { width: number; height: number }; showing?: string }) =>
    api<{ device: Screen; schedule: ScreenSchedule | null }>('/devices/heartbeat', { method: 'POST', json: body }),
  screens: () => api<{ devices: Screen[] } & ScreenSchedules>('/devices'),
  saveSchedules: (body: ScreenSchedules) => api<ScreenSchedules>('/devices/schedules', { method: 'PUT', json: body }),
  updateScreen: (deviceId: string, patch: ScreenPatch) =>
    api<{ device: Screen }>(`/devices/${encodeURIComponent(deviceId)}`, { method: 'PATCH', json: patch }),
  removeScreen: (deviceId: string) => api<void>(`/devices/${encodeURIComponent(deviceId)}`, { method: 'DELETE' }),
  microsoftStatus: () =>
    api<{ connected: boolean; email: string | null; tasks?: boolean; work?: boolean; workAccess?: boolean; accounts?: MicrosoftAccount[] }>(
      '/microsoft/status',
    ),
  /** One account, or every Microsoft account when none is given. */
  microsoftDisconnect: (account?: string) => api<void>(`/microsoft?${query({ account })}`, { method: 'DELETE' }),
  /** `drive` omitted = the account's own OneDrive; otherwise a SharePoint library / shared drive. `account` omitted = the first one. */
  driveChildren: (id?: string, drive?: string, name?: string, account?: string) =>
    api<DriveListing>(`/microsoft/drive/children?${query({ id, drive, name, account })}`),
  driveMedia: (folderId: string, recursive: boolean, drive?: string, account?: string) =>
    api<{ items: DriveMedia[]; truncated: boolean }>(
      `/microsoft/drive/media?${query({ folderId, recursive: recursive ? '1' : '0', drive, account })}`,
    ),
  driveLocations: (account?: string) => api<DriveLocations>(`/microsoft/drive/locations?${query({ account })}`),
  siteDrives: (siteId: string, account?: string) =>
    api<{ drives: Array<{ id: string; name: string }> }>(`/microsoft/sites/${encodeURIComponent(siteId)}/drives?${query({ account })}`),
  outlookCalendars: () => api<{ calendars: OutlookCalendar[] }>('/microsoft/calendars'),
  outlookCalendar: (days: number, hidden: string[], start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/microsoft/calendar?days=${days}&hidden=${encodeURIComponent(hidden.join(','))}${start ? `&start=${start}` : ''}`,
    ),
  pairStart: () =>
    api<{ pairingId: string; pollToken: string; code: string; expiresAt: string }>('/pair/start', { method: 'POST' }),
  pairPoll: (pairingId: string, pollToken: string) =>
    api<{ status: 'pending' | 'approved' | 'expired' }>('/pair/poll', { method: 'POST', json: { pairingId, pollToken } }),
  pairApprove: (code: string) => api<{ ok: true }>('/pair/approve', { method: 'POST', json: { code } }),
  reversePlace: (lat: number, lon: number, lang: string) =>
    api<{ place: Place | null }>(`/geo/reverse?${new URLSearchParams({ lat: String(lat), lon: String(lon), lang })}`),
  preview: (url: string) => api<{ image: string | null }>(`/feeds/preview?url=${encodeURIComponent(url)}`),
  rss: (url: string) => api<{ title: string; items: FeedItem[] }>(`/feeds/rss?url=${encodeURIComponent(url)}`),
  ics: (url: string, days: number, start?: string) =>
    api<{ events: CalendarEvent[] }>(
      `/feeds/ics?url=${encodeURIComponent(url)}&days=${days}&tz=${encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone)}${start ? `&start=${start}` : ''}`,
    ),
};
